// @ts-check
// Relatório da campanha de WhatsApp a partir de PREPARING (spec/whatsapp.md 18.4; contrato 09 de campanhas de WhatsApp,
// seção 15): faixa de situação (espera, pausa com o que fazer, fim), cartões de contatos, enviadas, entregues, lidas,
// respostas e falhas, cobradas pela Meta, saídas, progresso, falhas por motivo (com o reenvio do 131049), fora do envio,
// eventos e destinatários com filtros e CSV. Atualiza a cada 5 s enviando e a cada 60 s nas 48 h depois do fim.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import {
  cancelWhatsappCampaign, downloadWhatsappCampaignRecipients, duplicateWhatsappCampaign, getWhatsappCampaignReport, listWhatsappCampaignRecipients,
  pauseWhatsappCampaign, resendLimitedWhatsappCampaign, resumeWhatsappCampaign,
} from '../../data/whatsapp-campaigns.js';
import { ApiError } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatCurrency, formatDateTime, formatNumber, formatTime } from '../../lib/format.js';
import {
  RECIPIENT_FILTER_KEYS, canCancelCampaign, canPauseCampaign, describeCampaignState, percentOf, reportRefreshInterval,
} from '../../lib/whatsapp-campaigns.js';
import { usePolling } from '../conversations/conversations-state.js';
import { describeCampaignError } from './whatsapp-campaign-review-modal.js';
import { CampaignKindBadge, CampaignStatusBadge, whatsappCampaignHref, deliveryReasonText } from './whatsapp-campaigns-page.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaign} WhatsappCampaign
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaignReport} WhatsappCampaignReport
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaignRecipient} WhatsappCampaignRecipient
 */

// o htm tira o espaço que vem com a quebra de linha: o separador leva os próprios espaços
const SEPARATOR = ' · ';

// confirmações que a retomada pede por motivo da pausa
const RESUME_ACKNOWLEDGEMENTS = Object.freeze(/** @type {Record<string, 'acknowledgeLowQuality' | 'acknowledgeOptOutRate'>} */ ({
  TEMPLATE_QUALITY_RED: 'acknowledgeLowQuality', HIGH_OPT_OUT_RATE: 'acknowledgeOptOutRate',
}));

/** Texto de um código de erro da Meta. @param {number | null} code @param {string | null} errorClass */
export function errorCodeText(code, errorClass) {
  if (code === null) return errorClass === 'UNKNOWN_DELIVERY' ? t('whatsappCampaigns.errors.UNKNOWN_DELIVERY') : t('whatsappCampaigns.errors.unknown');
  const key = `whatsappCampaigns.errors.${code}`;
  return hasTranslation(key) ? t(key) : t('whatsappCampaigns.errors.other', { code });
}

/** Cartão de número do relatório. @param {{ label: string, value: number, rate?: number | null, tone?: string, testId?: string }} props */
function MetricCard({ label, value, rate = null, tone, testId }) {
  return html`<div class="wa-report-metric" data-tone=${tone} data-testid=${testId}>
    <span class="wa-report-metric-label">${label}</span>
    <strong class="wa-report-metric-value">${formatNumber(value)}</strong>
    ${rate !== null ? html`<span class="wa-report-metric-rate">${rate}%</span>` : null}
  </div>`;
}

/**
 * Retomar, com as confirmações que o motivo da pausa pede.
 * @param {{ campaign: WhatsappCampaign, reason: string | null, onClose: () => void, onDone: (campaign: WhatsappCampaign) => void }} props
 */
function ResumeDialog({ campaign, reason, onClose, onDone }) {
  const acknowledgementKey = reason ? RESUME_ACKNOWLEDGEMENTS[reason] : undefined;
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const confirm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await resumeWhatsappCampaign(campaign.id, acknowledgementKey ? { [acknowledgementKey]: isChecked } : {});
      showSuccessSnackbar(t('whatsappCampaigns.actions.resumed'));
      onDone(updated);
    } catch (requestError) {
      setError(describeCampaignError(requestError));
    } finally {
      setIsLoading(false);
    }
  };
  return html`<${Modal} title=${t('whatsappCampaigns.resume.title')} subtitle=${reason ? t(`whatsappCampaigns.pauseHelp.${reason}`) : undefined}
    confirmLabel=${t('whatsappCampaigns.actions.resume')} isConfirmDisabled=${Boolean(acknowledgementKey) && !isChecked} isLoading=${isLoading}
    onClose=${onClose} onConfirm=${confirm}>
    ${acknowledgementKey
      ? html`<div class="wa-review-ack"><${Checkbox} checked=${isChecked} onChange=${setIsChecked} ariaLabel=${t(`whatsappCampaigns.resume.${acknowledgementKey}`)} />
          <span onClick=${() => setIsChecked(!isChecked)}>${t(`whatsappCampaigns.resume.${acknowledgementKey}`)}</span></div>`
      : null}
    ${error ? html`<p class="wa-field-error" role="alert">${error}</p>` : null}
  <//>`;
}

/**
 * Tabela (ou lista no celular) dos destinatários, com os filtros de situação e "Carregar mais".
 * @param {{ campaignId: string, refreshKey: string }} props
 */
function RecipientsSection({ campaignId, refreshKey }) {
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState('ALL');
  const [recipients, setRecipients] = useState(/** @type {WhatsappCampaignRecipient[] | null} */ (null));
  const [cursor, setCursor] = useState(/** @type {string | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const canExport = hasCurrentPermissionFlag('EXPORT_CSV');
  useEffect(() => {
    listWhatsappCampaignRecipients(campaignId, { status: filter, limit: 50 })
      .then((page) => {
        setRecipients(page.recipients);
        setCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
        setError(null);
      })
      .catch((requestError) => setError(requestError instanceof ApiError && requestError.statusCode === 403 ? t('whatsappCampaigns.report.recipientsForbidden') : describeCampaignError(requestError)));
  }, [campaignId, filter, refreshKey]);
  const loadMore = () => {
    if (!cursor) return;
    listWhatsappCampaignRecipients(campaignId, { status: filter, limit: 50, startingAfter: cursor })
      .then((page) => {
        setRecipients([...(recipients ?? []), ...page.recipients]);
        setCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      })
      .catch(showErrorSnackbar);
  };
  const exportCsv = async () => {
    try {
      const file = await downloadWhatsappCampaignRecipients(campaignId);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName ?? 'campanha-whatsapp.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      showErrorSnackbar(requestError);
    }
  };
  /** @param {WhatsappCampaignRecipient} recipient */
  const timeOf = (recipient) => recipient.optedOutAt ?? recipient.repliedAt ?? recipient.readAt ?? recipient.deliveredAt ?? recipient.failedAt ?? recipient.acceptedAt;
  /** @param {WhatsappCampaignRecipient} recipient */
  const reasonOf = (recipient) => (recipient.skipReason
    ? deliveryReasonText(recipient.skipReason)
    : recipient.status === 'FAILED' ? errorCodeText(recipient.errorCode, recipient.errorClass) : '');
  /** @param {WhatsappCampaignRecipient} recipient */
  const nameOf = (recipient) => (recipient.isOutOfScope
    ? html`<span class="wa-report-out-of-scope">${t('whatsappCampaigns.report.outOfScope')}</span>`
    : recipient.personId
      ? html`<${Link} href=${`/object/person/${recipient.personId}`}>${recipient.displayName ?? t('whatsappCampaigns.report.noName')}<//>`
      : recipient.displayName ?? '');
  return html`<section class="wa-report-section" aria-labelledby="wa-report-recipients">
    <div class="wa-report-section-header">
      <h2 id="wa-report-recipients">${t('whatsappCampaigns.report.recipients')}</h2>
      ${canExport ? html`<${Button} title=${t('whatsappCampaigns.actions.export')} icon="IconDownload" variant="secondary" size="small" onClick=${exportCsv} />` : null}
    </div>
    <div class="wa-campaign-chips" role="group" aria-label=${t('whatsappCampaigns.report.recipientFilterLabel')}>
      ${RECIPIENT_FILTER_KEYS.map((key) => html`<button key=${key} type="button" class="wa-campaign-chip" aria-pressed=${key === filter ? 'true' : 'false'}
        onClick=${() => setFilter(key)}>${t(`whatsappCampaigns.report.recipientFilters.${key}`)}</button>`)}
    </div>
    ${error
      ? html`<p class="wa-campaign-state" role="alert">${error}</p>`
      : recipients === null
        ? html`<${Skeleton} height=${80} />`
        : recipients.length === 0
          ? html`<p class="wa-campaign-state">${t('whatsappCampaigns.report.noRecipients')}</p>`
          : isMobile
            ? html`<ul class="wa-campaign-cards">${recipients.map((recipient) => html`<li key=${recipient.id} class="wa-campaign-card wa-report-recipient-card">
                <span class="wa-campaign-name">${nameOf(recipient)}</span>
                <span>${recipient.phone ? `+${recipient.phone}` : ''}</span>
                <span><span class="wa-badge">${t(`whatsappCampaigns.recipientStatus.${recipient.status}`)}</span> ${reasonOf(recipient)}</span>
                ${recipient.messageThreadId ? html`<${Link} href=${`/conversations/${recipient.messageThreadId}`}>${t('whatsappCampaigns.report.openConversation')}<//>` : null}
              </li>`)}</ul>`
            : html`<div class="wa-campaign-table-wrapper"><table class="wa-campaign-table wa-report-recipients">
                <thead><tr>
                  <th scope="col">${t('whatsappCampaigns.report.columns.person')}</th>
                  <th scope="col">${t('whatsappCampaigns.report.columns.card')}</th>
                  <th scope="col">${t('whatsappCampaigns.report.columns.phone')}</th>
                  <th scope="col">${t('whatsappCampaigns.report.columns.status')}</th>
                  <th scope="col">${t('whatsappCampaigns.report.columns.reason')}</th>
                  <th scope="col">${t('whatsappCampaigns.report.columns.time')}</th>
                  <th scope="col"><span class="wa-visually-hidden">${t('whatsappCampaigns.report.openConversation')}</span></th>
                </tr></thead>
                <tbody>${recipients.map((recipient) => html`<tr key=${recipient.id}>
                  <td>${nameOf(recipient)}</td>
                  <td>${recipient.recordId ? html`<${Link} href=${`/object/enrollmentOpportunity/${recipient.recordId}`}>${recipient.recordName ?? ''}<//>` : recipient.recordName ?? ''}</td>
                  <td>${recipient.phone ? `+${recipient.phone}` : ''}</td>
                  <td><span class="wa-badge" data-tone=${recipient.status === 'FAILED' ? 'red' : recipient.status === 'READ' ? 'green' : undefined}>${t(`whatsappCampaigns.recipientStatus.${recipient.status}`)}</span></td>
                  <td>${reasonOf(recipient)}</td>
                  <td>${timeOf(recipient) ? formatDateTime(timeOf(recipient)) : ''}</td>
                  <td>${recipient.messageThreadId ? html`<${Link} href=${`/conversations/${recipient.messageThreadId}`}>${t('whatsappCampaigns.report.openConversation')}<//>` : null}</td>
                </tr>`)}</tbody>
              </table></div>`}
    ${cursor ? html`<div class="wa-campaign-more"><${Button} title=${t('whatsappCampaigns.list.loadMore')} variant="secondary" size="small" onClick=${loadMore} /></div>` : null}
  </section>`;
}

/** @param {{ campaign: WhatsappCampaign, onChanged: (campaign: WhatsappCampaign) => void }} props */
export function WhatsappCampaignReport({ campaign, onChanged }) {
  const isMobile = useIsMobile();
  const [report, setReport] = useState(/** @type {WhatsappCampaignReport | null} */ (null));
  const [loadError, setLoadError] = useState(/** @type {string | null} */ (null));
  const [isResumeOpen, setIsResumeOpen] = useState(searchParamsState.value.get('resume') === '1');
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const lastRefresh = useRef(Date.now());

  const load = () => getWhatsappCampaignReport(campaign.id)
    .then((value) => {
      setReport(value);
      setLoadError(null);
      lastRefresh.current = Date.now();
    })
    .catch((error) => setLoadError(describeCampaignError(error)));
  useEffect(() => {
    load();
  }, [campaign.id, campaign.status]);
  const interval = reportRefreshInterval({ status: report?.status ?? campaign.status, finishedAt: campaign.finishedAt });
  usePolling(() => {
    load();
    if (report && report.status !== campaign.status) onChanged({ ...campaign, status: report.status });
  }, interval ?? 60_000, interval !== null);
  useEffect(() => {
    if (report && report.status !== campaign.status) onChanged({ ...campaign, status: report.status, pauseReason: report.pauseReason, waitingReason: report.waitingReason });
  }, [report?.status]);

  /** @param {() => Promise<WhatsappCampaign>} operation @param {string} message */
  const act = async (operation, message) => {
    setIsWorking(true);
    try {
      const updated = await operation();
      showSuccessSnackbar(message);
      onChanged(updated);
      await load();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ACKNOWLEDGEMENT_REQUIRED') setIsResumeOpen(true);
      else showErrorSnackbar(error);
    } finally {
      setIsWorking(false);
    }
  };
  const duplicate = async () => {
    try {
      const copy = await duplicateWhatsappCampaign(campaign.id);
      navigate(whatsappCampaignHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  const resend = async () => {
    try {
      const result = await resendLimitedWhatsappCampaign(campaign.id);
      showSuccessSnackbar(t('whatsappCampaigns.report.resent', { count: result.requeued }));
      onChanged(result.campaign);
      await load();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const status = report?.status ?? campaign.status;
  const headerActions = html`<div class="wa-editor-actions">
    ${canPauseCampaign(status) ? html`<${Button} title=${t('whatsappCampaigns.actions.pause')} icon="IconPlayerPause" variant="secondary" size="small" isLoading=${isWorking}
      onClick=${() => act(() => pauseWhatsappCampaign(campaign.id), t('whatsappCampaigns.actions.paused'))} />` : null}
    ${status === 'PAUSED' ? html`<${Button} title=${t('whatsappCampaigns.actions.resume')} icon="IconPlayerPlay" accent="blue" size="small" onClick=${() => setIsResumeOpen(true)} />` : null}
    ${canCancelCampaign(status) ? html`<${Button} title=${t('whatsappCampaigns.actions.cancel')} icon="IconCircleX" variant="secondary" accent="danger" size="small" onClick=${() => setIsCancelOpen(true)} />` : null}
    <${Button} title=${t('whatsappCampaigns.actions.duplicate')} icon="IconCopy" variant="secondary" size="small" onClick=${duplicate} />
  </div>`;
  const breadcrumb = html`<a class="wa-editor-back" href="/campaigns/whatsapp" onClick=${(/** @type {MouseEvent} */ event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    navigate('/campaigns/whatsapp');
  }}><${Icon} name="IconChevronLeft" size=${14} /><span class="wa-editor-back-label">${t('whatsappCampaigns.title')}</span></a>`;
  const badges = html`<${CampaignKindBadge} kind=${campaign.kind} /><${CampaignStatusBadge} campaign=${{ ...campaign, status }} />`;

  const renderBody = () => {
    if (loadError) {
      return html`<div class="wa-campaign-state" role="alert"><p>${loadError}</p>
        <${Button} title=${t('whatsappCampaigns.list.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`;
    }
    if (!report) return html`<div class="wa-report-metrics" aria-busy="true">${[0, 1, 2, 3, 4, 5].map((index) => html`<${Skeleton} key=${index} height=${84} radius=${8} />`)}</div>`;
    const state = describeCampaignState({ ...report, nextStepAt: report.nextStepAt });
    const stateValues = {
      until: state.values.until ? formatDateTime(state.values.until) : '',
      time: state.values.until ? formatTime(state.values.until) : '',
      reason: state.values.reason ? (hasTranslation(`whatsappCampaigns.pauseReasons.${state.values.reason}`) ? t(`whatsappCampaigns.pauseReasons.${state.values.reason}`)
        : hasTranslation(`whatsappCampaigns.failureReasons.${state.values.reason}`) ? t(`whatsappCampaigns.failureReasons.${state.values.reason}`) : state.values.reason) : '',
      cards: formatNumber(report.counts.audienceCards ?? 0),
    };
    const { counts } = report;
    const progress = percentOf(report.progress.processed, report.progress.total) ?? 0;
    // contatos são quem a campanha tenta mandar; os de fora pela base ficam em "Fora do envio"
    const sendable = Math.max(0, counts.recipients - (counts.skipped ?? 0));
    const skipped = Object.entries(report.skippedByReason ?? {}).filter(([, count]) => count > 0).sort((left, right) => right[1] - left[1]);
    return html`
      <div class="wa-report-state" data-tone=${state.tone} role="status" data-testid="wa-report-state">
        <${Icon} name=${status === 'PAUSED' ? 'IconPlayerPause' : status === 'SENDING' || status === 'PREPARING' ? 'IconSend' : 'IconInfoCircle'} size=${16} />
        <div>
          <p><strong>${t(state.key, stateValues)}</strong></p>
          ${status === 'PAUSED' && report.pauseReason ? html`<p>${t(`whatsappCampaigns.pauseHelp.${report.pauseReason}`)}</p>` : null}
          ${report.marketingAppliedSince ? html`<p>${t('whatsappCampaigns.report.marketingApplied', { date: formatDateTime(report.marketingAppliedSince) })}</p>` : null}
        </div>
      </div>
      <div class="wa-report-metrics">
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.contacts')} value=${sendable} testId="wa-metric-contacts" />
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.sent')} value=${counts.accepted} rate=${percentOf(counts.accepted, sendable)} testId="wa-metric-sent" />
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.delivered')} value=${counts.delivered} rate=${percentOf(counts.delivered, counts.accepted)} tone="green" testId="wa-metric-delivered" />
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.read')} value=${counts.read} rate=${percentOf(counts.read, counts.delivered)} tone="blue" testId="wa-metric-read" />
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.replies')} value=${counts.replied} rate=${percentOf(counts.replied, counts.delivered)} testId="wa-metric-replies" />
        <${MetricCard} label=${t('whatsappCampaigns.report.cards.failures')} value=${counts.failed} tone="red" testId="wa-metric-failures" />
      </div>
      <p class="wa-report-line">
        ${t('whatsappCampaigns.report.billable', { count: formatNumber(counts.billable ?? 0), marketing: formatNumber(counts.billableMarketing ?? 0) })}
        ${report.cost?.isAvailable ? `${SEPARATOR}${t('whatsappCampaigns.report.cost', { total: formatCurrency(Number(report.cost.estimatedTotal ?? 0), report.cost.currency ?? 'BRL') })}` : null}
        ${SEPARATOR}<span data-testid="wa-report-opted-out">${t('whatsappCampaigns.report.optedOut', { count: formatNumber(counts.optedOut ?? 0) })}</span>
      </p>
      <div class="wa-report-progress">
        <div class="wa-limit-meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow=${progress}
          aria-label=${t('whatsappCampaigns.report.progress', { processed: formatNumber(report.progress.processed), total: formatNumber(report.progress.total) })}>
          <div class="wa-limit-meter-fill" style=${`width: ${progress}%`}></div>
        </div>
        <span>${t('whatsappCampaigns.report.progress', { processed: formatNumber(report.progress.processed), total: formatNumber(report.progress.total) })}</span>
        <span class="wa-report-updated">${t('whatsappCampaigns.report.lastUpdate', { time: formatTime(new Date(lastRefresh.current).toISOString()) })}</span>
      </div>
      ${report.recipientsPurged ? html`<p class="wa-notice" data-tone="info"><${Icon} name="IconArchive" size=${16} /><span>${t('whatsappCampaigns.report.recipientsPurged')}</span></p>` : null}
      <section class="wa-report-section" aria-labelledby="wa-report-failures">
        <h2 id="wa-report-failures">${t('whatsappCampaigns.report.failuresByCode')}</h2>
        ${report.failuresByCode.length === 0
          ? html`<p class="wa-campaign-state">${t('whatsappCampaigns.report.noFailures')}</p>`
          : html`<ul class="wa-report-failures">${report.failuresByCode.map((failure) => html`<li key=${`${failure.code}-${failure.errorClass}`}>
              <span class="wa-report-failure-text">${errorCodeText(failure.code === null || failure.code === undefined ? null : Number(failure.code), failure.errorClass)}</span>
              <strong>${formatNumber(failure.count)}</strong>
              ${Number(failure.code) === 131049
                ? html`<span class="wa-report-resend">
                    <${Button} title=${t('whatsappCampaigns.report.resendLimited')} icon="IconSend" variant="secondary" size="small"
                      disabled=${!failure.canResend || Boolean(failure.resendAvailableAt && Date.parse(failure.resendAvailableAt) > Date.now())} onClick=${resend} />
                    ${failure.resendAvailableAt && Date.parse(failure.resendAvailableAt) > Date.now()
                      ? html`<small>${t('whatsappCampaigns.report.resendAvailableAt', { date: formatDateTime(failure.resendAvailableAt) })}</small>`
                      : null}
                  </span>`
                : null}
            </li>`)}</ul>`}
      </section>
      ${skipped.length > 0
        ? html`<details class="wa-report-section wa-report-skipped">
            <summary>${t('whatsappCampaigns.report.skippedByReason', { count: formatNumber(skipped.reduce((total, [, count]) => total + count, 0)) })}</summary>
            <ul class="wa-review-exclusions">${skipped.map(([reason, count]) => html`<li key=${reason}><span>${deliveryReasonText(reason)}</span><strong>${formatNumber(count)}</strong></li>`)}</ul>
          </details>`
        : null}
      <section class="wa-report-section" aria-labelledby="wa-report-events">
        <h2 id="wa-report-events">${t('whatsappCampaigns.report.eventsTitle')}</h2>
        <ol class="wa-report-events">${report.events.map((event, index) => html`<li key=${`${event.kind}-${index}`}>
          <time datetime=${event.at}>${formatDateTime(event.at)}</time>
          <span>${hasTranslation(`whatsappCampaigns.report.events.${event.kind}`)
            ? t(`whatsappCampaigns.report.events.${event.kind}`, {
              reason: event.details?.reason && hasTranslation(`whatsappCampaigns.pauseReasons.${event.details.reason}`) ? t(`whatsappCampaigns.pauseReasons.${event.details.reason}`) : String(event.details?.reason ?? ''),
              count: Number(event.details?.recipients ?? event.details?.requeued ?? event.details?.remaining ?? 0),
              until: event.details?.until ? formatDateTime(event.details.until) : '',
            })
            : event.kind}</span>
          ${event.actorName ? html`<span class="wa-report-event-actor">${event.actorName}</span>` : null}
        </li>`)}</ol>
      </section>
      <${RecipientsSection} campaignId=${campaign.id} refreshKey=${`${report.lastUpdatedAt}-${counts.delivered}-${counts.read}-${counts.replied}`} />`;
  };

  return html`<${PageCard} className="campaigns-screen wa-campaign-report-page"
    header=${html`<${PageHeader} icon="IconBrandWhatsapp" breadcrumb=${breadcrumb} actions=${isMobile ? null : headerActions} title=${html`<span class="wa-editor-title">
      <span class="wa-report-name">${campaign.name}</span>${isMobile ? null : badges}
    </span>`} />`}
  >
    ${isMobile ? html`<div class="wa-mobile-toolbar">${badges}${headerActions}</div>` : null}
    <div class="wa-report">${renderBody()}</div>
    ${isResumeOpen
      ? html`<${ResumeDialog} campaign=${campaign} reason=${report?.pauseReason ?? campaign.pauseReason} onClose=${() => {
          setIsResumeOpen(false);
          if (searchParamsState.value.get('resume')) updateSearchParams({ resume: null });
        }}
          onDone=${(/** @type {WhatsappCampaign} */ updated) => {
            setIsResumeOpen(false);
            if (searchParamsState.value.get('resume')) updateSearchParams({ resume: null });
            onChanged(updated);
            load();
          }} />`
      : null}
    ${isCancelOpen
      ? html`<${Modal} title=${t('whatsappCampaigns.confirm.cancelTitle', { name: campaign.name })} subtitle=${t('whatsappCampaigns.confirm.cancel')}
          confirmLabel=${t('whatsappCampaigns.actions.cancel')} confirmAccent="danger" isLoading=${isWorking} onClose=${() => setIsCancelOpen(false)}
          onConfirm=${async () => {
            await act(() => cancelWhatsappCampaign(campaign.id), t('whatsappCampaigns.actions.canceled'));
            setIsCancelOpen(false);
          }} />`
      : null}
  <//>`;
}
